import { Skeleton, Table, Typography } from "antd";
import dayjs from "dayjs";
import { useReducer, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router";
import { apiDateFormat } from "~constants/apiDateFormat.ts";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useFetchSeason } from "~hooks/apiHooks/useFetchSeason";
import { useHandlePagination } from "~hooks/useHandlePagination";
import type { AssignFixtureOfficialsTableRow } from "~pages/Officials/AssignFixtureOfficialsPage/types/assignFixtureOfficialsTableRow";
import type { SeasonResponse } from "~types/season";
import { onTableChange } from "~utilities/onTableChange";
import styles from "./AssignFixtureOfficialsPage.module.css";
import { AssignFixtureOfficialsFilters } from "./components/AssignFixtureOfficialsFilters";
import { AssignFixtureOfficialsModal } from "./components/AssignFixtureOfficialsModal";
import { useAssignFixtureOfficialsColumns } from "./hooks/useAssignFixtureOfficialsColumns";
import { useFetchSeasonFixtures } from "./hooks/useFetchSeasonFixtures";
import type { AssignFixtureOfficialsFiltersType } from "./types/assignFixtureOfficialsFilters";
import { transformFixtureResponse } from "./utilities/transformFixtureResponse";

const filtersInitialState: AssignFixtureOfficialsFiltersType = {
  fromTimeLocal: null,
  fixtureType: undefined,
  status: undefined,
};

const DEFAULT_SORT_FIELD = "startTimeLocal";

const AssignFixtureOfficialsPage = () => {
  const [fixtureId, setFixtureId] = useState<string | null>(null);

  const { formatMessage } = useIntl();

  const [filters, setFilters] = useReducer(
    (filters: AssignFixtureOfficialsFiltersType, newFilters: Partial<AssignFixtureOfficialsFiltersType>) => ({
      ...filters,
      ...newFilters,
    }),
    filtersInitialState,
  );

  const { seasonId = "" } = useParams();
  const {
    data: seasonDates,
    isLoading: isSeasonLoading,
    isFetching: isSeasonFetching,
  } = useFetchSeason(
    seasonId,
    undefined,
    (seasonData: SeasonResponse) => {
      return {
        ...(seasonData.data[0]?.startDate && {
          fromTimeLocal: dayjs(seasonData.data[0].startDate).format(apiDateFormat),
        }),
        ...(seasonData.data[0]?.endDate && {
          toTimeLocal: dayjs(seasonData.data[0].endDate).set("hour", 23).set("minute", 59).set("second", 59).format(apiDateFormat),
        }),
      };
    },
    ["seasonStartEndDates"],
    {
      enabled: !!seasonId,
    },
  );

  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination();
  const [sortBy, setSortBy] = useState<string>(DEFAULT_SORT_FIELD);
  const params: Record<string, string> = {
    limit: pageSize.toString(),
    offset: offset.toString(),
    ...(filters.fromTimeLocal
      ? {
          fromTimeLocal: filters.fromTimeLocal.format(apiDateFormat),
          toTimeLocal: filters.fromTimeLocal.add(1, "day").format(apiDateFormat),
        }
      : seasonDates),
    ...(filters.fixtureType && { fixtureType: filters.fixtureType }),
    ...(filters.status && { status: filters.status }),
    ...(filters.venue?.value && { venueId: filters.venue.value }),
    ...(filters.stage?.value && { stageCode: filters.stage.value }),
    ...(filters.pool?.value && { poolCode: filters.pool.value }),
    ...(filters.series?.value && { seriesCode: filters.series.value }),
    sortBy,
    include: ["seasons", "entities", "persons", "venues", "seasonPools", "seasonStages", "seasonSeries"].join(","),
  };

  const {
    data,
    isLoading: isFixtureLoading,
    isFetching: isFixtureFetching,
  } = useFetchSeasonFixtures(params, transformFixtureResponse(seasonId), {
    enabled: !!seasonDates,
  });
  const tableData = data?.tableData;

  const isLoading = isSeasonLoading || isFixtureLoading;
  const isFetching = isSeasonFetching || isFixtureFetching;

  const columns = useAssignFixtureOfficialsColumns(setFixtureId);

  const onChange = onTableChange<AssignFixtureOfficialsTableRow>(setSortBy, DEFAULT_SORT_FIELD);

  const { hasPermission } = useUserPermissions();
  const createAllowed = hasPermission(PermissionNames.ROLES, PermissionOperations.CREATE);
  const readAllowed = hasPermission(PermissionNames.PERSON, PermissionOperations.READ);
  const formAllowed = createAllowed && readAllowed;

  return (
    <div>
      {formAllowed && fixtureId && <AssignFixtureOfficialsModal fixtureId={fixtureId} closeModal={() => setFixtureId(null)} />}
      <div className={styles.topBar}>
        <div className={styles.topLeftContainer}>
          <Typography.Title level={4} className={styles.title}>
            {isLoading ? <Skeleton.Input active className={styles.skeleton} /> : formatMessage({ id: "officials.assign.title" })}
          </Typography.Title>
        </div>
      </div>
      <AssignFixtureOfficialsFilters
        filters={filters}
        setFilters={setFilters}
        isFetching={isFetching}
        seasonDates={seasonDates}
        className={styles.filters}
      />
      <Table
        size="small"
        dataSource={tableData}
        loading={isFetching}
        columns={columns}
        rowKey="fixtureId"
        rootClassName={styles.tableContainer}
        onChange={onChange}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: pageSizeOptions,
          showQuickJumper: false,
          total: offset + (tableData?.length ?? 0) + 1,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: () => formatMessage({ id: "pageN" }, { number: currentPage }),
        }}
      />
    </div>
  );
};

export default AssignFixtureOfficialsPage;
